import {
  isCanonicalIssueViewerQueryKey,
  type IssueViewerQueryIdentifier,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useQueryClient} from '@github-ui/react-query'
import {getHighlightedEvent, isEventHighlighted} from '@github-ui/timeline-items/HighlightedEvent'
import {useCallback, useMemo, useState} from 'react'

import {getInitialTimelinePageSize} from '../../constants/values'
import {reconcileCanonicalTimelineSnapshot} from './fetch-canonical-timeline-snapshot'
import {
  ACTIVITY_TIMELINE_FEEDS,
  type TimelineFeed,
  type TimelineIssue,
  type TimelineItemsConnection,
  type TimelineItemsPageParams,
  timelineItemsQueryKey,
  timelineItemsQueryKeyPrefix,
  type TimelineItemTypes,
  timelineQueryOptions,
} from './fetch-timeline-items'
import type {TimelineItem} from './IssueTimeline'
import type {IssueTimelineItemFragment} from './IssueTimelineItem'
import {
  assembleTimelineItems,
  countItemsRemaining,
  dedupeItems,
  isHighlightReady,
  subtractItems,
  type TimelineCounters,
  type TimelineSlot,
} from './timeline-assembly'
import {
  anchoredBackParamsFor,
  asPinnedTimelineWindow,
  frontParamsFor,
  unanchoredBackParamsFor,
} from './timeline-prefill'
import {overlaid, pendingCountDelta, withCorrelationKeys} from './timeline-write-overlay'
import {type TimelineSection, useLoadedPages, useTimelineSections} from './use-timeline-sections'
import {useTimelineWrites} from './use-timeline-writes'

export type UseTimelineItemsResult = {
  timelineItems: TimelineItem[] | null
  totalItemCount: number
  highlightedReady: boolean
  isLoading?: boolean
  commentNavigationState?: 'loading' | 'unavailable' | 'error'
  retryCommentNavigation?: () => void
  newestSectionQueryKey?: readonly unknown[]
  onCanonicalTimelineUpdated?: () => void | Promise<void>
}

export type UseTimelineItemsTanstackProps = {
  owner: string
  repo: string
  number: number
  /** Owner of the timeline connection for optimistic overlays. */
  issueId: string
  primaryIdentifier?: IssueViewerQueryIdentifier
  primaryQueryKey?: readonly unknown[]
  feed?: TimelineFeed
  /** Narrows the activity feed. Callers remount the hook when it changes; see `IssueTimeline`. */
  itemTypes?: TimelineItemTypes
  highlightedEvent?: string
  /** Offset-based crawler page from `?timeline_page=N`. */
  page?: number
  /** Matches the Relay path's first page size so both produce the same initial view. */
  pageSize?: number
  /**
   * Cursor the newest section pins itself to, from the page payload. Without one the section falls
   * back to discovering it, which costs the round trip hydration exists to remove.
   */
  initialBackAnchor?: string
  /** Reads only the focused window and its subsequently requested cursor pages. */
  focusedOnly?: boolean
}

type LoadMoreOptions = {onComplete?: () => void}

const connectionOf = (issue: TimelineIssue | undefined) => issue?.timelineItems ?? undefined

const isPresent = <T>(value: T | undefined): value is T => value !== undefined

const FOCUS_NEIGHBOR_COUNT = 3

function edgeNodes(connection: TimelineItemsConnection | undefined): IssueTimelineItemFragment[] {
  if (!connection) return []

  return (connection.edges ?? []).flatMap(edge => (edge?.node ? [edge.node] : []))
}

/**
 * The section holding the end of the timeline, and so the one a comment the reader posts belongs
 * in. Its pages, opened with "load more", read older rows and are not the end.
 */
const isEndSection = (section: TimelineSection | undefined) => Boolean(section?.isBase) && section?.role === 'back'

/** The permalinked comment with its neighbours, and whatever "load more" has opened either side. */
function highlightWindowItems(
  focused: TimelineItemsConnection | undefined,
  before: TimelineItemsConnection[],
  after: TimelineItemsConnection[],
): IssueTimelineItemFragment[] {
  if (!focused) return []

  return dedupeItems([...before.flatMap(edgeNodes), ...edgeNodes(focused), ...after.flatMap(edgeNodes)])
}

/**
 * Extend the focus window only by unique earlier rows: re-anchoring can overlap retained pages.
 */
function highlightStartPosition(
  beforeFocusCount: number,
  before: TimelineItemsConnection[],
  focused: TimelineItemsConnection | undefined,
): number {
  const earlierItems = subtractItems(dedupeItems(before.flatMap(edgeNodes)), edgeNodes(focused))
  return Math.max(beforeFocusCount - earlierItems.length, 0)
}

/**
 * Selects live timeline sections and assembles their rows, preserving the Relay hook's result shape.
 * Interactive timelines use oldest/newest windows and an optional focus window; focused Activity
 * uses only the focus window, while crawler pages read one isolated page.
 *
 * See [Timeline architecture](../../README.mdx#timeline-architecture) for section queries, anchoring,
 * retained-window recovery, and cache ownership.
 */
export function useTimelineItemsTanstack({
  owner,
  repo,
  number,
  issueId,
  primaryIdentifier,
  primaryQueryKey,
  feed = 'activity',
  itemTypes,
  highlightedEvent,
  page,
  pageSize = getInitialTimelinePageSize(),
  initialBackAnchor,
  focusedOnly = false,
}: UseTimelineItemsTanstackProps): UseTimelineItemsResult {
  const queryClient = useQueryClient()
  const effectivePrimaryIdentifier = useMemo(
    () => primaryIdentifier ?? {owner, repo, number},
    [number, owner, primaryIdentifier, repo],
  )
  const highlight = useMemo(() => getHighlightedEvent(highlightedEvent), [highlightedEvent])
  const focusText = highlight ? `${highlight.prefix}-${highlight.id}` : undefined
  const issueKey = `${owner}/${repo}/${number}`
  const [commentWindow, setCommentWindow] = useState({issueKey, requestedFocus: focusText, anchor: focusText})
  const windowFocusText = feed === 'comments' && commentWindow.issueKey === issueKey ? commentWindow.anchor : focusText
  const frontParams = useMemo(() => frontParamsFor(pageSize), [pageSize])

  const queryOptions = useCallback(
    (params: TimelineItemsPageParams) => timelineQueryOptions(owner, repo, number, params, feed, itemTypes),
    [owner, repo, number, feed, itemTypes],
  )

  const loadedPages = useLoadedPages(windowFocusText)

  // Without a payload anchor, probe for an extra row to pin the newest window.
  // Once discovered, keep that anchor rather than moving the reader's window with later payloads.
  const [discovered, setDiscovered] = useState<{issueKey: string; cursor: string}>()
  // Kept per issue: the hosts that discover their own anchor are also the ones that re-render this
  // hook with a different issue rather than remounting it, and a cursor names a row in one timeline
  // only. Carried across, it would pin the next issue's window to a row from the last one.
  const discoveredBackAnchor = discovered?.issueKey === issueKey ? discovered.cursor : undefined
  const backAnchor = discoveredBackAnchor ?? initialBackAnchor

  const crawlerParams: TimelineItemsPageParams | undefined = useMemo(
    () => (page ? {timelinePage: page} : undefined),
    [page],
  )

  // Unanchored until an extra row exists outside the visible window. Built by the same functions
  // the prefill writes under, so a stored window is found rather than fetched again under a key
  // differing by a character.
  const backParams: TimelineItemsPageParams = useMemo(
    () => (backAnchor ? anchoredBackParamsFor(backAnchor) : unanchoredBackParamsFor(pageSize)),
    [backAnchor, pageSize],
  )

  // In the order they render: the oldest rows, then the permalink window sitting in the gap, then
  // the newest. `select` reads back along this order, so it is the section order, not a listing.
  const sections = useMemo(
    (): TimelineSection[] =>
      focusedOnly && windowFocusText
        ? [
            ...loadedPages.highlight.filter(section => section.role === 'highlight-before'),
            {
              role: 'highlight' as const,
              params: {focusText: windowFocusText, first: 1, focusNeighborCount: FOCUS_NEIGHBOR_COUNT},
              isBase: true,
            },
            ...loadedPages.highlight.filter(section => section.role === 'highlight-after'),
          ]
        : crawlerParams
          ? [{role: 'front', params: crawlerParams, isBase: true}]
          : [
              {role: 'front', params: frontParams, isBase: true},
              ...loadedPages.front.map((params): TimelineSection => ({role: 'front', params})),
              ...loadedPages.highlight.filter(section => section.role === 'highlight-before'),
              ...(windowFocusText
                ? [
                    {
                      role: 'highlight' as const,
                      params: {focusText: windowFocusText, first: 1, focusNeighborCount: FOCUS_NEIGHBOR_COUNT},
                      isBase: true,
                    },
                  ]
                : []),
              ...loadedPages.highlight.filter(section => section.role === 'highlight-after'),
              // Pages read backward from the newest end are older than it, so they render before it.
              ...loadedPages.back.map((params): TimelineSection => ({role: 'back', params})),
              {role: 'back', params: backParams, isBase: true},
            ],
    [
      focusedOnly,
      crawlerParams,
      frontParams,
      backParams,
      windowFocusText,
      loadedPages.front,
      loadedPages.back,
      loadedPages.highlight,
    ],
  )

  const {reads, loading, fetching, errors, select, baseIndex} = useTimelineSections({
    owner,
    repo,
    number,
    feed,
    itemTypes,
    sections,
    // A retained anchor can recover after deletion; an unavailable selected comment cannot.
    recoverMissingFocus: !focusedOnly && (feed !== 'comments' || windowFocusText !== focusText),
    requiredBaseRole: focusedOnly && windowFocusText ? 'highlight' : 'front',
  })
  const frontBaseIndex = baseIndex('front')
  const fetchedFrontConnection = connectionOf(reads[frontBaseIndex])
  const highlightBaseIndex = baseIndex('highlight')
  const fetchedHighlightConnection = connectionOf(reads[highlightBaseIndex])

  const backBaseIndex = baseIndex('back')
  const backBaseRead = reads[backBaseIndex]
  const backBaseConnection = connectionOf(backBaseRead)

  const isLoading = focusedOnly
    ? !fetchedHighlightConnection && Boolean(loading[highlightBaseIndex])
    : !fetchedFrontConnection && !backBaseConnection && Boolean(loading[frontBaseIndex] || loading[backBaseIndex])

  // Only from a read of the issue being rendered. Switching issues re-renders this hook before the
  // reads catch up, so for one render the newest section still holds the previous issue's rows --
  // and taking a cursor from those would pin this issue's window to a row in someone else's
  // timeline.
  const backBaseEdges = backBaseConnection?.edges ?? []
  if (
    backBaseRead?.id === issueId &&
    !backAnchor &&
    backBaseEdges.length === pageSize + 1 &&
    backBaseConnection?.pageInfo?.startCursor
  ) {
    const cursor = backBaseConnection.pageInfo.startCursor
    // Re-key atomically: without a value under the anchored key, the unanchored read disappears
    // for the render that starts the pinned read. That can briefly leave only the stale oldest
    // count, or no rows at all. Marking this snapshot stale preserves the completed probe while
    // still making the pinned query confirm it with the endpoint immediately.
    queryClient.setQueryData<TimelineIssue>(
      timelineItemsQueryKey(owner, repo, number, anchoredBackParamsFor(cursor), feed, itemTypes),
      {...backBaseRead, timelineItems: asPinnedTimelineWindow(backBaseConnection)},
      {updatedAt: 0},
    )
    setDiscovered({issueKey, cursor})
  }

  // The newest section always holds the end of the timeline, so it is always where a write goes.
  // Memoized because it is handed to the live-update subscription, whose Relay config is compared by
  // identity: a fresh array each render tears the subscription down and re-establishes it.
  const endSectionQueryKey = useMemo(
    () => timelineItemsQueryKey(owner, repo, number, backParams, feed, itemTypes),
    [owner, repo, number, backParams, feed, itemTypes],
  )
  const timelineKeyPrefix = useMemo(
    () => timelineItemsQueryKeyPrefix(owner, repo, number, feed, itemTypes),
    [owner, repo, number, feed, itemTypes],
  )
  const inactiveTimelineKeyPrefixes = useMemo(
    () =>
      feed === 'comments'
        ? ACTIVITY_TIMELINE_FEEDS.map(activityFeed => timelineItemsQueryKeyPrefix(owner, repo, number, activityFeed))
        : undefined,
    [owner, repo, number, feed],
  )
  const reconcileTimelineAliveUpdate = useCallback(async () => {
    if (feed === 'activity-summary') {
      await queryClient.invalidateQueries({queryKey: timelineKeyPrefix})
      return
    }
    try {
      await reconcileCanonicalTimelineSnapshot({
        coordinates: {owner, repo, number},
        primaryIdentifier: effectivePrimaryIdentifier,
        issueId,
        primaryQueryKey,
        newestSectionQueryKey: endSectionQueryKey,
        queryClient,
      })
    } catch {
      // Keep the subscription healthy after a failed authoritative read. The snapshot query
      // records its error and the primary document converges on its next successful update.
    }
  }, [
    effectivePrimaryIdentifier,
    endSectionQueryKey,
    feed,
    issueId,
    number,
    owner,
    primaryQueryKey,
    queryClient,
    repo,
    timelineKeyPrefix,
  ])

  // A read has come back with the row this add is still pending for.
  const {endSectionOperations, everySectionOperations, correlationKeys} = useTimelineWrites({
    issueId,
    coordinates: {owner, repo, number},
    primaryIdentifier: effectivePrimaryIdentifier,
    primaryQueryKey,
    feed,
    itemTypes,
    endSectionQueryKey,
    timelineKeyPrefix,
    inactiveTimelineKeyPrefixes,
    reads,
  })

  // Where a comment the reader posted appears before the server has confirmed it: the pending
  // operation is applied over the rows as they are read. `applyOperations` does the inserting, at
  // the position the mutation asked for -- `{at: 'end'}` for a new comment.
  const connections = useMemo(
    () =>
      reads.map((read, index) => {
        const operations = isEndSection(sections[index]) ? endSectionOperations : everySectionOperations
        return withCorrelationKeys(connectionOf(overlaid(read, operations)), correlationKeys)
      }),
    [reads, sections, endSectionOperations, everySectionOperations, correlationKeys],
  )

  const frontPages = useMemo(() => select(connections, 'front').filter(isPresent), [select, connections])

  const frontItems = useMemo(() => dedupeItems(frontPages.flatMap(edgeNodes)), [frontPages])

  // The newest section's own total, moved by whatever is still pending. The pending part is summed
  // from the operations rather than read off an overlaid section, because an overlay moves a count
  // only for a section that held the row.
  const serverCount =
    connectionOf(reads[baseIndex('back')])?.totalCount ??
    (focusedOnly ? fetchedHighlightConnection?.totalCount : undefined)
  const totalItemCount = Math.max(
    (serverCount ?? fetchedFrontConnection?.totalCount ?? 0) + pendingCountDelta(endSectionOperations, reads),
    0,
  )

  const backPages = useMemo(() => select(connections, 'back').filter(isPresent), [select, connections])

  const backIsLoading = loading[baseIndex('back')] ?? false

  const backItems = useMemo(
    () => subtractItems(dedupeItems(backPages.flatMap(edgeNodes)), frontItems),
    [backPages, frontItems],
  )

  const loadedItems = useMemo(() => [...frontItems, ...backItems], [frontItems, backItems])

  const highlightIsPreloaded = useMemo(
    () => (highlight ? loadedItems.some(event => isEventHighlighted(event, highlight)) : false),
    [loadedItems, highlight],
  )

  const highlightIndex = highlightBaseIndex
  const highlightData = connections[highlightIndex]

  const highlightBefore = useMemo(
    () => select(connections, 'highlight-before').filter(isPresent),
    [select, connections],
  )
  const highlightAfter = useMemo(() => select(connections, 'highlight-after').filter(isPresent), [select, connections])

  const highlightItems = useMemo(
    () => highlightWindowItems(highlightData, highlightBefore, highlightAfter),
    [highlightData, highlightBefore, highlightAfter],
  )
  const focusedHighlightReady = useMemo(
    () => Boolean(highlight && highlightItems.some(event => isEventHighlighted(event, highlight))),
    [highlight, highlightItems],
  )

  if (
    feed === 'comments' &&
    (commentWindow.issueKey !== issueKey ||
      commentWindow.requestedFocus !== focusText ||
      (focusText !== undefined && windowFocusText === undefined && !highlightIsPreloaded && !focusedHighlightReady))
  ) {
    // Moving the highlight within loaded rows must not replace their query anchor or cursor pages.
    const reuseWindow = commentWindow.issueKey === issueKey && (highlightIsPreloaded || focusedHighlightReady)
    setCommentWindow({issueKey, requestedFocus: focusText, anchor: reuseWindow ? windowFocusText : focusText})
  }

  const startPosition = highlightStartPosition(
    connectionOf(reads[highlightIndex])?.beforeFocusCount ?? 0,
    highlightBefore,
    highlightData,
  )

  const uniqueHighlightItems = useMemo(
    () => (highlightItems.length > 0 ? subtractItems(highlightItems, loadedItems) : []),
    [highlightItems, loadedItems],
  )

  const hasLazyLoadedHighlights = uniqueHighlightItems.length > 0
  const focusUnavailable = focusedOnly && fetchedHighlightConnection !== undefined && !focusedHighlightReady
  const commentNavigationState =
    feed !== 'comments' || crawlerParams !== undefined || !highlight || highlightIsPreloaded || focusedHighlightReady
      ? undefined
      : fetching[highlightIndex]
        ? 'loading'
        : errors[highlightIndex]
          ? 'error'
          : fetchedHighlightConnection
            ? 'unavailable'
            : 'loading'
  const retryCommentNavigation = useCallback(() => {
    void queryClient.invalidateQueries({queryKey: timelineKeyPrefix})
  }, [queryClient, timelineKeyPrefix])

  const timelineCounters: TimelineCounters = useMemo(
    () => ({
      frontItems: frontItems.length,
      backItems: backItems.length,
      highlightItems: uniqueHighlightItems.length,
      highlightStartPosition: startPosition,
    }),
    [frontItems.length, backItems.length, uniqueHighlightItems.length, startPosition],
  )

  const {front: itemsRemainingFront, back: itemsRemainingBack} = countItemsRemaining({
    hasLazyLoadedHighlights,
    timelineCounters,
    totalItemCount,
    frontItemCount: frontItems.length,
    backItemCount: backItems.length,
  })

  // One request per activation; the endpoint clamps the page size it is given.
  const loadPages = useCallback(
    (
      count: number,
      /** The page to continue from: the one furthest in the direction being read. */
      from: TimelineItemsConnection | undefined,
      paramsFor: (from: TimelineItemsConnection, size: number) => TimelineItemsPageParams | null,
      apply: (params: TimelineItemsPageParams) => void,
      options?: LoadMoreOptions,
    ) => {
      void (async () => {
        const params = from && paramsFor(from, count)
        // `LoadMore` clears its own loading state only in `onComplete`, so every path has to reach
        // it -- including this one, or the control stays disabled with nothing in flight.
        if (!params) return options?.onComplete?.()

        try {
          const connection = connectionOf(await queryClient.fetchQuery(queryOptions(params)))
          // The rows are already in the cache; holding the params is what keeps the page live.
          if (connection && (connection.edges ?? []).length > 0) apply(params)
        } catch {
          // Keep the marker in place so the read can be retried.
        }

        options?.onComplete?.()
      })()
    },
    [queryClient, queryOptions],
  )

  const loadMoreFrontItems = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      loadPages(
        count,
        frontPages.at(-1),
        (from, size) =>
          from.pageInfo?.hasNextPage && from.pageInfo.endCursor ? {first: size, after: from.pageInfo.endCursor} : null,
        loadedPages.addFront,
        options,
      )
    },
    [loadPages, frontPages, loadedPages.addFront],
  )

  const loadMoreBackItems = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      loadPages(
        count,
        backPages.at(0),
        // Not `hasPreviousPage`: the resolver only counts that when `last` slices what the cursors
        // selected, so an anchored section reports false however many rows precede it.
        (from, size) => (from.pageInfo?.startCursor ? {last: size, before: from.pageInfo.startCursor} : null),
        loadedPages.addBack,
        options,
      )
    },
    [loadPages, backPages, loadedPages.addBack],
  )

  const loadBeforeHighlight = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      if (!highlightData) return options?.onComplete?.()

      // Continues from the last page read, not from the highlight section's own cursor, which
      // would refetch the same page on every activation.
      loadPages(
        count,
        highlightBefore.at(0) ?? highlightData,
        (from, size) =>
          from.pageInfo?.hasPreviousPage && from.pageInfo.startCursor
            ? {last: size, before: from.pageInfo.startCursor}
            : null,
        loadedPages.addHighlight('before'),
        options,
      )
    },
    [loadPages, highlightData, highlightBefore, loadedPages],
  )

  const loadAfterHighlight = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      if (!highlightData) return options?.onComplete?.()

      loadPages(
        count,
        highlightAfter.at(-1) ?? highlightData,
        (from, size) =>
          from.pageInfo?.hasNextPage && from.pageInfo.endCursor ? {first: size, after: from.pageInfo.endCursor} : null,
        loadedPages.addHighlight('after'),
        options,
      )
    },
    [loadPages, highlightData, highlightAfter, loadedPages],
  )

  const slots: TimelineSlot[] | null = useMemo(
    () =>
      focusUnavailable
        ? []
        : assembleTimelineItems({
            frontItems,
            backItems,
            emptyBackItems: [],
            uniqueLoadedHighlightItems: uniqueHighlightItems,
            totalItemCount,
            itemsRemainingFront,
            itemsRemainingBack,
            hasLazyLoadedHighlights,
            loadingInitialBackItems: backIsLoading,
            page,
          }),
    [
      focusUnavailable,
      frontItems,
      backItems,
      uniqueHighlightItems,
      totalItemCount,
      itemsRemainingFront,
      itemsRemainingBack,
      hasLazyLoadedHighlights,
      backIsLoading,
      page,
    ],
  )

  const timelineItems = useMemo(
    () =>
      slots?.map(slot => {
        if (slot.type !== 'load-marker') return slot

        const {type: _marker, ...rest} = slot
        return slot.position === 'top'
          ? {
              ...rest,
              type: 'load' as const,
              loadFromTop: focusedOnly ? loadBeforeHighlight : loadMoreFrontItems,
              loadFromBottom: focusedOnly || hasLazyLoadedHighlights ? loadBeforeHighlight : loadMoreBackItems,
            }
          : {
              ...rest,
              type: 'load' as const,
              loadFromTop: loadAfterHighlight,
              loadFromBottom: focusedOnly ? loadAfterHighlight : loadMoreBackItems,
            }
      }) ?? null,
    [
      slots,
      focusedOnly,
      hasLazyLoadedHighlights,
      loadMoreFrontItems,
      loadMoreBackItems,
      loadBeforeHighlight,
      loadAfterHighlight,
    ],
  )

  return {
    timelineItems,
    totalItemCount,
    isLoading,
    commentNavigationState,
    retryCommentNavigation,
    highlightedReady: focusedOnly
      ? focusedHighlightReady
      : isHighlightReady(totalItemCount, backItems.length, timelineCounters, highlightIsPreloaded, pageSize),
    // Only while the section is anchored. Before a timeline is long enough to supply an anchor, its
    // newest key can still change once the extra row arrives. Narrowing to that key would leave the
    // oldest section stale and can briefly produce a nonzero count with no rows during the re-key.
    newestSectionQueryKey: !focusedOnly && backAnchor ? endSectionQueryKey : undefined,
    // The snapshot is unfiltered, so a filtered feed refetches instead.
    onCanonicalTimelineUpdated:
      feed === 'activity-summary' ||
      (!focusedOnly &&
        feed === 'activity' &&
        !itemTypes &&
        primaryQueryKey &&
        isCanonicalIssueViewerQueryKey(primaryQueryKey))
        ? reconcileTimelineAliveUpdate
        : undefined,
  }
}
